// Client-side walker for the decision tree. Mirrors the server-side replay
// validation in backend/src/diagnostic/tree.rs — the server re-validates
// everything, this exists purely for wizard UX (current node, back, progress).
import { computed, ref, type ComputedRef, type Ref } from 'vue'
import type { Answer, AnswerValue, DecisionTree, TreeNode } from './types'

export interface DiagnosticEngine {
    currentId: Ref<string | null>
    currentNode: ComputedRef<TreeNode | null>
    /** Set when the walk landed on an outcome terminal (troubleshooting trees). */
    outcome: ComputedRef<TreeNode | null>
    answers: Ref<Answer[]>
    done: ComputedRef<boolean>
    progress: ComputedRef<number>
    canGoBack: ComputedRef<boolean>
    answer: (value: AnswerValue) => void
    back: () => void
    reset: () => void
}

/** Successor for a node given the answer just recorded. */
function nextOf(node: TreeNode, value: AnswerValue): string | null {
    if (node.kind === 'single_select' && typeof value === 'string') {
        const opt = node.options?.find((o) => o.value === value)
        if (opt && opt.next !== undefined) return opt.next
    }
    return node.next ?? null
}

/** Estimated remaining depth, for a stable progress bar. */
function estimateDepth(tree: DecisionTree, fromId: string | null): number {
    let depth = 0
    let id = fromId
    const seen = new Set<string>()
    while (id && !seen.has(id) && depth < 20) {
        seen.add(id)
        const node = tree.nodes[id]
        if (!node) break
        depth += 1
        id = node.next ?? node.options?.[0]?.next ?? null
    }
    return depth
}

export function useDiagnosticEngine(tree: DecisionTree): DiagnosticEngine {
    const currentId = ref<string | null>(tree.start)
    const answers = ref<Answer[]>([])

    const currentNode = computed(() =>
        currentId.value ? (tree.nodes[currentId.value] ?? null) : null,
    )
    const outcome = computed(() =>
        currentNode.value?.kind === 'outcome' ? currentNode.value : null,
    )
    // Done = the path ended: either past the last node (wizard trees) or on
    // an outcome terminal, which presents a resolution and takes no answer.
    const done = computed(
        () => (currentId.value === null || outcome.value !== null) && answers.value.length > 0,
    )

    const progress = computed(() => {
        const answered = answers.value.length
        if (done.value || outcome.value) return 1
        const remaining = estimateDepth(tree, currentId.value)
        return answered / Math.max(answered + remaining, 1)
    })

    const canGoBack = computed(() => answers.value.length > 0)

    function answer(value: AnswerValue) {
        const id = currentId.value
        if (!id) return
        const node = tree.nodes[id]
        if (!node) return
        answers.value = [...answers.value, { node_id: id, value }]
        currentId.value = nextOf(node, value)
    }

    function back() {
        const prev = answers.value.at(-1)
        if (!prev) return
        answers.value = answers.value.slice(0, -1)
        currentId.value = prev.node_id
    }

    function reset() {
        answers.value = []
        currentId.value = tree.start
    }

    return { currentId, currentNode, outcome, answers, done, progress, canGoBack, answer, back, reset }
}
